import { useState, type FormEvent } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { cn } from '../lib/cn';
import { DEMO, api, ApiFehler } from '../lib/api';
import { formatZahl, parseEuroEingabe, parseZahlEingabe } from '../lib/format';
import {
  type Einheit,
  type Struktur,
  DACHGESCHOSS,
  demoHausSpeichern,
  geschossKurz,
  geschossLang,
  kennzahlenAus,
  leereBelegung,
} from '../lib/haus';
import { transition, uebergangSanft } from '../lib/motion';

/**
 * Objekt anlegen und Stammdaten bearbeiten. Pflicht sind nur Bezeichnung, Anschrift und
 * Gesamtfläche; alles Weitere (Mietspiegel, Finanzierung, Ausstattung, Energieausweis)
 * liegt eingeklappt unter „Weitere Angaben“.
 */

const feldKlasse =
  'w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg outline-none focus:border-accent';

export const AUSSTATTUNG: Array<[string, string]> = [
  ['aufzug', 'Aufzug'],
  ['solarthermie', 'Solarthermie'],
  ['pv', 'Photovoltaik'],
  ['batteriespeicher', 'Batteriespeicher'],
  ['waermepumpe', 'Wärmepumpe'],
  ['klima', 'Klimaanlage'],
  ['fussbodenheizung', 'Fußbodenheizung'],
  ['balkon', 'Balkon/Terrasse'],
  ['einbaukueche', 'Einbauküche'],
  ['stellplatz', 'Stellplatz/Garage'],
  ['keller', 'Keller'],
  ['barrierefrei', 'Barrierefrei'],
  ['glasfaser', 'Glasfaser'],
];
export const AUSST_LABEL: Record<string, string> = Object.fromEntries(AUSSTATTUNG);
const ENERGIEKLASSEN = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

export type Objektart = 'efh' | 'etw' | 'mfh' | 'wgh';

export const OBJEKTART: Array<{ id: Objektart; titel: string; text: string }> = [
  { id: 'efh', titel: 'Einfamilienhaus', text: 'Ein Haus, eine Mietpartei' },
  { id: 'etw', titel: 'Eigentumswohnung', text: 'Einzelne Wohnung in einer WEG' },
  { id: 'mfh', titel: 'Mehrfamilienhaus', text: 'Mehrere Wohnungen, auch mit Einliegerwohnung' },
  { id: 'wgh', titel: 'Wohn- und Geschäftshaus', text: 'Wohnungen und Gewerbeeinheiten' },
];
export const OBJEKTART_LABEL: Record<Objektart, string> = Object.fromEntries(OBJEKTART.map((a) => [a.id, a.titel])) as Record<Objektart, string>;

/** Objektart; bei Bestandsobjekten ohne Angabe aus der Zahl der Einheiten abgeleitet. */
export function objektartVon(o: Pick<ApiObjektVoll, 'objektart' | 'einheitenAnzahl'>): Objektart | null {
  if (o.objektart) return o.objektart;
  if (o.einheitenAnzahl === undefined || o.einheitenAnzahl === 0) return null;
  return o.einheitenAnzahl === 1 ? 'efh' : 'mfh';
}

/** Etagen zur Auswahl für eine Eigentumswohnung. */
const ETW_ETAGEN = [-1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, DACHGESCHOSS];

/** Objekt wie von GET /objekte/:id geliefert. */
export interface ApiObjektVoll {
  id: string;
  bezeichnung: string;
  objektart?: Objektart | null;
  weg?: boolean;
  strasse: string;
  plz: string;
  ort: string;
  anschrift: string;
  baujahr: number | null;
  gesamtflaecheQm: number;
  co2Kennwert: number | null;
  bundesland: string | null;
  mietspiegelQmCent: number | null;
  kappungsgrenzeProzent: number | null;
  darlehenRestCent: number | null;
  sollzinsProzent: number | null;
  tilgungProzent: number | null;
  ausstattung: string[];
  ausstattungFrei: string | null;
  energieausweisArt: string | null;
  energieklasse: string | null;
  energieKwh: number | null;
  energieausweisGueltigBis: string | null;
  untergeschosse: number;
  obergeschosse: number | null;
  dachgeschoss: boolean;
  einheitenAnzahl?: number;
  vermietetAnzahl?: number;
  eigennutzungAnzahl?: number;
}

const zahlText = (n: number | null | undefined) => (n === null || n === undefined ? '' : formatZahl(n));
const euroText = (c: number | null | undefined) => (c === null || c === undefined ? '' : formatZahl(c / 100));
const zahlAus = (t: string) => parseZahlEingabe(t) ?? Number.NaN;


export function ObjektFormular({
  bestand,
  onGespeichert,
  onAbbruch,
}: {
  /** Vorhandenes Objekt: Bearbeiten statt Anlegen. */
  bestand?: ApiObjektVoll;
  /** einheitAngelegt: Einfamilienhaus/Eigentumswohnung wurde direkt mit ihrer Einheit angelegt. */
  onGespeichert: (o: ApiObjektVoll, info?: { einheitAngelegt: boolean }) => void;
  onAbbruch: () => void;
}) {
  const reduziert = useReducedMotion();
  const b = bestand;
  const [art, setArt] = useState<Objektart>(b ? (objektartVon(b) ?? 'mfh') : 'mfh');
  const [etwGeschoss, setEtwGeschoss] = useState(1);
  const einzeln = art === 'efh' || art === 'etw';
  const [bezeichnung, setBezeichnung] = useState(b?.bezeichnung ?? '');
  const [strasse, setStrasse] = useState(b?.strasse ?? '');
  const [plz, setPlz] = useState(b?.plz ?? '');
  const [ort, setOrt] = useState(b?.ort ?? '');
  const [baujahr, setBaujahr] = useState(b?.baujahr ? String(b.baujahr) : '');
  const [flaeche, setFlaeche] = useState(zahlText(b?.gesamtflaecheQm));
  const [co2, setCo2] = useState(zahlText(b?.co2Kennwert));
  const [bundesland, setBundesland] = useState(b?.bundesland ?? '');
  const [mietspiegel, setMietspiegel] = useState(euroText(b?.mietspiegelQmCent));
  const [kappung, setKappung] = useState(String(b?.kappungsgrenzeProzent ?? 20));
  const [darlehen, setDarlehen] = useState(euroText(b?.darlehenRestCent));
  const [sollzins, setSollzins] = useState(zahlText(b?.sollzinsProzent));
  const [tilgung, setTilgung] = useState(zahlText(b?.tilgungProzent));
  const [ausstattung, setAusstattung] = useState<string[]>(b?.ausstattung ?? []);
  const [ausstattungFrei, setAusstattungFrei] = useState(b?.ausstattungFrei ?? '');
  const [energieausweisArt, setEnergieausweisArt] = useState(b?.energieausweisArt ?? '');
  const [energieklasse, setEnergieklasse] = useState(b?.energieklasse ?? '');
  const [energieKwh, setEnergieKwh] = useState(zahlText(b?.energieKwh));
  const [gueltigBis, setGueltigBis] = useState(b?.energieausweisGueltigBis ?? '');
  const [weitere, setWeitere] = useState(false);
  const [sendet, setSendet] = useState(false);

  function toggleAusstattung(code: string) {
    setAusstattung((liste) =>
      liste.includes(code) ? liste.filter((x) => x !== code) : [...liste, code],
    );
  }
  const [fehler, setFehler] = useState<string | null>(null);

  async function speichern(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    const flaecheZahl = zahlAus(flaeche);
    const baujahrZahl = baujahr.trim() ? Number(baujahr.trim()) : null;
    if (baujahrZahl !== null && !(baujahrZahl >= 1500 && baujahrZahl <= 2200)) {
      setFehler('Bitte ein gültiges Baujahr angeben.');
      return;
    }
    if (!bezeichnung.trim() || !strasse.trim() || !/^\d{4,5}$/.test(plz.trim()) || !ort.trim() || !(flaecheZahl > 0)) {
      setFehler('Bitte Bezeichnung, Anschrift (mit gültiger PLZ) und eine Gesamtfläche größer null angeben.');
      return;
    }
    const co2Zahl = co2.trim() ? zahlAus(co2) : null;
    const land = bundesland.trim().toUpperCase() || null;
    const mietspiegelCent = mietspiegel.trim() ? parseEuroEingabe(mietspiegel) : null;

    const daten = {
      bezeichnung: bezeichnung.trim(),
      objektart: art,
      ...(art === 'etw' ? { weg: true } : b ? {} : { weg: false }),
      strasse: strasse.trim(),
      plz: plz.trim(),
      ort: ort.trim(),
      baujahr: baujahrZahl,
      gesamtflaecheQm: flaecheZahl,
      co2Kennwert: co2Zahl,
      bundesland: land,
      mietspiegelQmCent: mietspiegelCent,
      kappungsgrenzeProzent: kappung === '15' ? 15 : 20,
      darlehenRestCent: darlehen.trim() ? parseEuroEingabe(darlehen) : null,
      sollzinsProzent: sollzins.trim() ? zahlAus(sollzins) : null,
      tilgungProzent: tilgung.trim() ? zahlAus(tilgung) : null,
      ausstattung,
      ausstattungFrei: ausstattungFrei.trim() || null,
      energieausweisArt: (energieausweisArt || null) as 'verbrauch' | 'bedarf' | null,
      energieklasse: energieklasse || null,
      energieKwh: energieKwh.trim() ? zahlAus(energieKwh) : null,
      energieausweisGueltigBis: gueltigBis || null,
    };

    // Einfamilienhaus und Eigentumswohnung: Aufbau und die eine Einheit direkt mit anlegen.
    const struktur: Struktur | null = b
      ? null
      : art === 'efh'
        ? { untergeschosse: 0, obergeschosse: 0, dachgeschoss: false }
        : art === 'etw'
          ? {
              untergeschosse: etwGeschoss < 0 ? -etwGeschoss : 0,
              obergeschosse: etwGeschoss > 0 && etwGeschoss !== DACHGESCHOSS ? etwGeschoss : 0,
              dachgeschoss: etwGeschoss === DACHGESCHOSS,
            }
          : null;
    const einheit = struktur
      ? {
          bezeichnung: art === 'efh' ? 'Einfamilienhaus' : `Wohnung ${geschossKurz(etwGeschoss)}`,
          geschoss: art === 'efh' ? 0 : etwGeschoss,
          wohnflaecheQm: flaecheZahl,
          nutzungsart: 'wohnen' as const,
        }
      : null;

    setSendet(true);
    try {
      if (DEMO && struktur && einheit) {
        const id = `demo-${Date.now()}`;
        const e: Einheit = { id: `${id}-e1`, objektId: id, zimmer: null, lage: null, megAnteil: null, eigennutzung: false, position: null, reihenfolge: 0, ...einheit };
        const imHaus = [{ ...e, belegung: leereBelegung(), zaehlerAnzahl: 0 }];
        demoHausSpeichern({
          objekt: {
            id,
            bezeichnung: daten.bezeichnung,
            anschrift: `${daten.strasse}, ${daten.plz} ${daten.ort}`,
            gesamtflaecheQm: daten.gesamtflaecheQm,
            baujahr: daten.baujahr,
            mietspiegelQmCent: daten.mietspiegelQmCent,
            kappungsgrenzeProzent: daten.kappungsgrenzeProzent,
            objektart: art,
            ...struktur,
          },
          einheiten: imHaus,
          kennzahlen: kennzahlenAus(imHaus),
        });
        onGespeichert(
          {
            id,
            anschrift: `${daten.strasse}, ${daten.plz} ${daten.ort}`,
            ...struktur,
            einheitenAnzahl: 1,
            vermietetAnzahl: 0,
            eigennutzungAnzahl: 0,
            ...daten,
          },
          { einheitAngelegt: true },
        );
        return;
      }
      if (DEMO) {
        onGespeichert({
          id: b?.id ?? `demo-${Date.now()}`,
          anschrift: `${daten.strasse}, ${daten.plz} ${daten.ort}`,
          untergeschosse: b?.untergeschosse ?? 0,
          obergeschosse: b?.obergeschosse ?? null,
          dachgeschoss: b?.dachgeschoss ?? false,
          einheitenAnzahl: b?.einheitenAnzahl ?? 0,
          vermietetAnzahl: b?.vermietetAnzahl ?? 0,
          eigennutzungAnzahl: b?.eigennutzungAnzahl ?? 0,
          ...daten,
        });
        return;
      }
      const antwort = b
        ? await api.patch<{ objekt: ApiObjektVoll }>(`/objekte/${b.id}`, daten)
        : await api.post<{ objekt: ApiObjektVoll }>('/objekte', {
            ...Object.fromEntries(Object.entries(daten).filter(([, v]) => v !== null && v !== '')),
          });
      if (struktur && einheit) {
        try {
          await api.post(`/objekte/${antwort.objekt.id}/etagen`, { ...struktur, neueEinheiten: [einheit] });
          onGespeichert({ ...antwort.objekt, ...struktur, einheitenAnzahl: 1, vermietetAnzahl: 0, eigennutzungAnzahl: 0 }, { einheitAngelegt: true });
        } catch {
          // Objekt ist angelegt; die Einheit lässt sich im Assistenten nachholen.
          onGespeichert(antwort.objekt, { einheitAngelegt: false });
        }
        return;
      }
      onGespeichert(antwort.objekt);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Das Objekt konnte nicht gespeichert werden.');
    } finally {
      setSendet(false);
    }
  }

  return (
    <Card titel={b ? 'Stammdaten bearbeiten' : 'Neues Objekt'} className="mb-4">
      <form onSubmit={speichern} className="space-y-4">
        <fieldset>
          <legend className="mb-1.5 block text-sm font-medium text-fg">Objektart</legend>
          <div role="radiogroup" aria-label="Objektart" className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            {OBJEKTART.map((a) => {
              const aktiv = art === a.id;
              return (
                <button
                  key={a.id}
                  type="button"
                  role="radio"
                  aria-checked={aktiv}
                  onClick={() => setArt(a.id)}
                  className={cn(
                    'relative min-w-0 rounded-lg border px-3 py-2.5 text-left transition-colors duration-fast',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
                    aktiv ? 'border-accent bg-accent-soft' : 'border-border bg-surface hover:bg-surface-2',
                  )}
                >
                  {aktiv && (
                    <motion.span
                      layoutId="objektart-rahmen"
                      className="pointer-events-none absolute inset-0 rounded-lg ring-2 ring-accent"
                      transition={reduziert ? { duration: 0 } : { type: 'spring', stiffness: 500, damping: 38 }}
                      aria-hidden="true"
                    />
                  )}
                  <span className={cn('block text-sm font-semibold', aktiv ? 'text-accent-soft-fg' : 'text-fg')}>{a.titel}</span>
                  <span className="mt-0.5 block text-xs leading-snug text-fg-muted">{a.text}</span>
                </button>
              );
            })}
          </div>
          {!b && (
            <p className="mt-2 text-xs text-fg-muted">
              {einzeln
                ? 'Die Einheit wird direkt mit angelegt. Danach erfassen Sie Mieter, Zähler und Kosten.'
                : 'Im nächsten Schritt legen Sie Etagen und Einheiten an.'}
            </p>
          )}
        </fieldset>
        <Input label="Bezeichnung" placeholder={art === 'etw' ? 'z. B. ETW Lindenstraße 14, 2. OG' : 'z. B. Lindenstraße 14'} value={bezeichnung} onChange={(e) => setBezeichnung(e.target.value)} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label="Straße und Hausnummer" value={strasse} onChange={(e) => setStrasse(e.target.value)} />
          <div className="grid grid-cols-[7rem_1fr] gap-3">
            <Input label="PLZ" inputMode="numeric" value={plz} onChange={(e) => setPlz(e.target.value)} />
            <Input label="Ort" value={ort} onChange={(e) => setOrt(e.target.value)} />
          </div>
        </div>
        <div className={cn('grid grid-cols-1 gap-4', !b && art === 'etw' ? 'sm:grid-cols-4' : 'sm:grid-cols-3')}>
          <Input
            label={einzeln ? 'Wohnfläche (m²)' : 'Wohn-/Nutzfläche gesamt (m²)'}
            inputMode="decimal"
            value={flaeche}
            onChange={(e) => setFlaeche(e.target.value)}
            hinweis={art === 'etw' ? 'Fläche der Wohnung laut Mietvertrag.' : undefined}
          />
          {!b && art === 'etw' && (
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-fg">Lage im Haus</span>
              <select className={feldKlasse} value={etwGeschoss} onChange={(e) => setEtwGeschoss(Number(e.target.value))}>
                {ETW_ETAGEN.map((g) => (
                  <option key={g} value={g}>
                    {geschossLang(g)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <Input label="Baujahr" inputMode="numeric" placeholder="optional" value={baujahr} onChange={(e) => setBaujahr(e.target.value)} />
          <Input label="Bundesland" placeholder="z. B. BW" value={bundesland} onChange={(e) => setBundesland(e.target.value)} />
        </div>

        <button
          type="button"
          onClick={() => setWeitere((w) => !w)}
          aria-expanded={weitere}
          className="flex w-full items-center justify-between rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-left text-sm font-medium text-fg hover:bg-surface-3"
        >
          <span>
            Weitere Angaben <span className="font-normal text-fg-muted">(optional: CO₂, Mietspiegel, Finanzierung, Ausstattung, Energieausweis)</span>
          </span>
          <motion.span animate={{ rotate: weitere ? 180 : 0 }} aria-hidden="true">
            ▾
          </motion.span>
        </button>

        <AnimatePresence initial={false}>
        {weitere && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={transition(!!reduziert, uebergangSanft)}
          className="space-y-4 overflow-hidden px-px"
        >
        <Input
          label="CO₂-Kennwert (kg/m²a)"
          inputMode="decimal"
          placeholder="optional"
          hinweis="Aus dem Energieausweis bzw. der Heizkostenabrechnung; bestimmt die CO₂-Kostenaufteilung."
          value={co2}
          onChange={(e) => setCo2(e.target.value)}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_14rem]">
          <Input
            label="Mietspiegel / ortsübliche Vergleichsmiete (€/m²)"
            inputMode="decimal"
            placeholder="optional"
            value={mietspiegel}
            onChange={(e) => setMietspiegel(e.target.value)}
          />
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Kappungsgrenze (§ 558)</span>
            <select className={feldKlasse} value={kappung} onChange={(e) => setKappung(e.target.value)}>
              <option value="20">20 % (Regelfall)</option>
              <option value="15">15 % (angespannter Markt)</option>
            </select>
          </label>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Input
            label="Darlehen Restschuld (€)"
            inputMode="decimal"
            placeholder="optional"
            value={darlehen}
            onChange={(e) => setDarlehen(e.target.value)}
          />
          <Input label="Sollzins (% p. a.)" inputMode="decimal" placeholder="optional" value={sollzins} onChange={(e) => setSollzins(e.target.value)} />
          <Input label="Tilgung (% p. a.)" inputMode="decimal" placeholder="optional" value={tilgung} onChange={(e) => setTilgung(e.target.value)} />
        </div>
        <p className="-mt-2 text-xs text-fg-subtle">
          Finanzierung ist optional — sie treibt den Cashflow nach Kapitaldienst in der Auswertung.
        </p>

        <div>
          <span className="mb-1.5 block text-sm font-medium text-fg">Ausstattung / Anlagentechnik</span>
          <div className="flex flex-wrap gap-2">
            {AUSSTATTUNG.map(([code, label]) => {
              const aktiv = ausstattung.includes(code);
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => toggleAusstattung(code)}
                  className={
                    'rounded-lg border px-3 py-1.5 text-sm ' +
                    (aktiv
                      ? 'border-accent bg-accent-soft text-accent-soft-fg'
                      : 'border-border-strong text-fg-muted hover:bg-surface-2 hover:text-fg')
                  }
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
        <Input
          label="Weitere Ausstattung (Freitext, optional)"
          value={ausstattungFrei}
          onChange={(e) => setAusstattungFrei(e.target.value)}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Energieausweis</span>
            <select className={feldKlasse} value={energieausweisArt} onChange={(e) => setEnergieausweisArt(e.target.value)}>
              <option value="">—</option>
              <option value="verbrauch">Verbrauchsausweis</option>
              <option value="bedarf">Bedarfsausweis</option>
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Energieklasse</span>
            <select className={feldKlasse} value={energieklasse} onChange={(e) => setEnergieklasse(e.target.value)}>
              <option value="">—</option>
              {ENERGIEKLASSEN.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </label>
          <Input label="Endenergie (kWh/m²·a)" inputMode="decimal" placeholder="optional" value={energieKwh} onChange={(e) => setEnergieKwh(e.target.value)} />
          <Input label="Gültig bis" type="date" value={gueltigBis} onChange={(e) => setGueltigBis(e.target.value)} />
        </div>
        </motion.div>
        )}
        </AnimatePresence>

        {fehler && <p className="text-sm font-medium text-danger">{fehler}</p>}

        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={sendet}>
            {sendet ? 'Wird gespeichert …' : b ? 'Änderungen speichern' : einzeln ? 'Objekt anlegen' : 'Objekt anlegen und weiter'}
          </Button>
          <Button type="button" variante="dezent" onClick={onAbbruch}>
            Abbrechen
          </Button>
        </div>
      </form>
    </Card>
  );
}


export default ObjektFormular;
